import { AirtableRecord } from '@/modules/core/data/types/AirtableRecord';
import { Campus } from '@/modules/core/data/types/Campus';
import { ChatbotContext } from '@/modules/core/data/types/ChatbotContext';
import { EducationalProgram } from '@/modules/core/data/types/EducationalProgram';
import { EducationalProgramStep } from '@/modules/core/data/types/EducationalProgramStep';
import { ModChatbot } from '@/modules/core/data/types/ModChatbot';
import { StudentSolutionsCategory } from '@/modules/core/data/types/StudentSolutionsCategory';
import every from 'lodash.every';
import find from 'lodash.find';
import size from 'lodash.size';
import { filterStepsByPrograms } from '../../solutions/educationalProgramStep';

/**
 * Resolves whether all required questions have been answered
 *
 * @return {boolean}
 */
const areAllQuestionsAnswered = (context: ChatbotContext, allEducationalPrograms: EducationalProgram[], allEducationalProgramSteps: EducationalProgramStep[], allCampuses: Campus[], allStudentSolutionsCategories: StudentSolutionsCategory[], modChatbot: AirtableRecord<ModChatbot>): boolean => {
  const hasSelectedPersona = size(context?.persona) >= 1;
  const hasSelectedEducationalSteps = size(context?.educationalProgramSteps) >= 1;
  const hasSelectedCampuses = size(context?.campuses) >= 1;
  const hasSelectedCategories = size(context?.categories) >= 1;
  const isCategoryQuestionEnabled = modChatbot?.isCategoryQuestionEnabled;
  const isThereAnyCategoryChoice = !!size(allStudentSolutionsCategories);

  // Initialise required checks that don't depend on the customer's data
  // Then, add additional checks if there are more than one available choice
  const checks: { field: string; hasSelectedValue: boolean }[] = [
    {
      field: 'persona',
      hasSelectedValue: hasSelectedPersona,
    },
  ];

  // If there are more than one program available and at least one available step, then we check if the user has selected a step
  if (size(allEducationalPrograms) > 1 && size(allEducationalProgramSteps) > 0) {
    const selectedProgram: EducationalProgram = find(allEducationalPrograms, (educationalProgram: EducationalProgram) => educationalProgram?.ref === context?.educationalPrograms?.[0]?.ref);
    const availableStepsForSelectedProgram = filterStepsByPrograms(selectedProgram?.steps, context?.educationalPrograms);

    // If no program has been selected, or if the selected program contains more than one step, then we check if the user has selected at least one step
    if (
      size(allEducationalPrograms) > 1 ||
      (size(context.educationalPrograms) === 0 || size(availableStepsForSelectedProgram) > 1)
    ) {
      checks.push({
        field: 'educationalProgramSteps',
        hasSelectedValue: hasSelectedEducationalSteps,
      });
    }
  }

  // Check presence of any campuses if there are any available
  if (size(allCampuses) > 1) {
    checks.push({
      field: 'campuses',
      hasSelectedValue: hasSelectedCampuses,
    });
  }

  if (isCategoryQuestionEnabled && isThereAnyCategoryChoice) {
    // Check presence of any category if there are any available
    if (size(allStudentSolutionsCategories) > 1) {
      checks.push({
        field: 'categories',
        hasSelectedValue: hasSelectedCategories,
      });
    }
  }

  return every(checks, { hasSelectedValue: true });
};

export default areAllQuestionsAnswered;
